// Port of Flutter's devicesScreen.dart — a room's devices.
//
//   AppBar  avatar · room name · location name
//   body    the same common-devices strip as the Rooms screen, then the room's
//           device grid (maxCrossAxisExtent 250, aspect 0.9, spacing 10)
//   FAB     Add Device
//
// Like the Dart, opening a device subscribes its `<uno>/client` topic first so
// live status is already flowing by the time the control screen builds.

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import DeviceCard from '../components/DeviceCard';
import ExtentGrid from '../components/ExtentGrid';
import CircleMenu from '../components/CircleMenu';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import { useApp } from '../../../core/state/AppContext';
import { getDevicesByRoom, getCommonDevices } from '../../../core/api/api';
import { subscribeTopic } from '../../../core/mqtt/mqttClient';

export default function MobileDevicesScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { state } = useLocation();
  const { userId, toggleFavourite, canUpdateFirmware } = useApp();

  const [devices, setDevices] = useState(null);
  const [common, setCommon] = useState([]);
  const [updateOpen, setUpdateOpen] = useState(false);

  const roomName = state?.roomName || 'Room';
  const locationName = state?.locationName || '';
  const locationId = state?.locationId;

  const load = useCallback(async () => {
    if (!userId) return;
    const [ds, cs] = await Promise.all([
      getDevicesByRoom(userId, roomId).catch(() => null),
      locationId ? getCommonDevices(userId, locationId).catch(() => null) : Promise.resolve(null),
    ]);
    setDevices(Array.isArray(ds?.data) ? ds.data : []);
    setCommon(Array.isArray(cs?.data) ? cs.data : []);
  }, [userId, roomId, locationId]);

  useEffect(() => { load(); }, [load]);

  const openDevice = (d) => {
    subscribeTopic(`${d.UniqueNumber}/client`);
    navigate(`/device/${d.UniqueNumber}`, { state: { device: d } });
  };
  const onStar = async (d, fav) => { await toggleFavourite(d, fav); load(); };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <ProfileTitleBar title={roomName} subtitle={locationName} />

      {/* PINNED — same two-container split as the Rooms screen: the common strip
          stays put and scrolls sideways, only the device grid scrolls down. */}
      <CommonDevicesStrip devices={common} onOpen={openDevice} onToggleFavourite={onStar} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box sx={{ p: 1 }}>
          {devices == null ? null : devices.length === 0 ? (
            <Typography sx={{ color: c.text, textAlign: 'center' }}>No Devices Found</Typography>
          ) : (
            <ExtentGrid maxExtent={250} childAspectRatio={0.9} gap={10}>
              {devices.map((d) => (
                <DeviceCard
                  key={d.UniqueNumber}
                  device={d}
                  onOpen={openDevice}
                  onToggleFavourite={onStar}
                />
              ))}
            </ExtentGrid>
          )}
        </Box>
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Device',
            onClick: () => navigate(`/add-device?locationId=${locationId ?? ''}&roomId=${roomId}`),
          },
          // Onboard a brand-new device: find its setup hotspot and hand it the
          // Wi-Fi it should join. The location travels along so the picker can
          // offer the networks already in use at this site.
          {
            icon: <WifiIcon />,
            title: 'Set Up New Device',
            onClick: () => navigate(`/wifi-setup?locationId=${locationId ?? ''}`),
          },
          // Room-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Room)',
            onClick: () => setUpdateOpen(true),
          }] : []),
        ]}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'room', id: roomId, name: roomName }}
        onClose={() => setUpdateOpen(false)}
      />
    </Box>
  );
}
